/**
 * Restrict BOLD-uploaded file option fields to approved image/design formats.
 * Applies to regular site product forms only (skips builder overlay inputs).
 */

import { ACCEPTED_UPLOAD_FILE_TYPES, IMAGE_PROPERTY_KEY_SET } from '../shared/uploadFieldConfig';

const mimeTypes = new Set(
    ACCEPTED_UPLOAD_FILE_TYPES.split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value && !value.startsWith('.'))
);

const extensions = new Set(
    ACCEPTED_UPLOAD_FILE_TYPES.split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.startsWith('.'))
);

function isRegularSiteInput(input) {
    return !input.closest('.minifig-builder');
}

function getPropertyKey(input) {
    const name = input.getAttribute('name') || '';
    const matches = name.match(/^properties\[(.*)\]$/);
    return matches ? matches[1] : null;
}

function isAllowedFile(file) {
    if (!file) return true;

    const fileType = (file.type || '').toLowerCase();
    const fileName = (file.name || '').toLowerCase();
    const extensionIndex = fileName.lastIndexOf('.');
    const fileExtension = extensionIndex >= 0 ? fileName.slice(extensionIndex) : '';

    if (fileType && mimeTypes.has(fileType)) return true;
    if (fileExtension && extensions.has(fileExtension)) return true;

    return false;
}

function validateSelectedFiles(input) {
    const files = Array.from(input.files || []);
    const invalidFile = files.find((file) => !isAllowedFile(file));

    if (!invalidFile) {
        input.setCustomValidity('');
        return;
    }

    input.value = '';
    input.setCustomValidity(`Unsupported file type. Allowed: ${ACCEPTED_UPLOAD_FILE_TYPES}`);
    input.reportValidity();

    // Clear custom validity so future valid selections submit normally.
    requestAnimationFrame(() => input.setCustomValidity(''));
}

function applyRestriction(input) {
    if (!input || input.dataset.uploadTypeRestricted === 'true') return;
    if (!isRegularSiteInput(input)) return;

    const key = getPropertyKey(input);
    if (!key || !IMAGE_PROPERTY_KEY_SET.has(key)) return;

    input.setAttribute('accept', ACCEPTED_UPLOAD_FILE_TYPES);
    input.dataset.uploadTypeRestricted = 'true';
    input.addEventListener('change', () => validateSelectedFiles(input));
}

function applyToExistingInputs(root = document) {
    const fileInputs = root.querySelectorAll('input[type="file"][name^="properties["]');
    fileInputs.forEach(applyRestriction);
}

function boldUploadFileTypes() {
    applyToExistingInputs();

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof Element)) return;

                if (node.matches('input[type="file"][name^="properties["]')) {
                    applyRestriction(node);
                    return;
                }

                if (node.querySelector) {
                    applyToExistingInputs(node);
                }
            });
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', () => {
        applyToExistingInputs();
    });
}

export default boldUploadFileTypes;
